import { difficultyCounts, pickRandomClip } from '../services/earTrainingMelodyBank';
import type { EarTrainingClip } from '../types/earTraining';
import { EarTrainingPlayer } from '../utils/earTrainingPlayback';
import {
  INSTRUMENT_LABELS,
  loadEarTrainingPreferences,
  saveEarTrainingPreferences,
  type EarTrainingInstrument,
  type EarTrainingPreferences,
} from '../utils/earTrainingPreferences';
import { renderEarTrainingStaff } from '../utils/earTrainingStaff';
import {
  buildPaletteForClip,
  buildUserAnswerPlaybackNotes,
  checkSolfegeAnswer,
  getClipSolfegeAnswer,
  isObscureSyllable,
} from '../utils/earTrainingSolfegeAnswer';
import { escapeHtml } from '../utils/escapeHtml';
import { formatHyphenLine } from '../utils/earTrainingDisplay';
import {
  formatSolfegeTokensLineHtml,
  renderSolfegeTokenHtml,
} from '../utils/earTrainingSolfegeDisplay';
import { midiToNoteNameInKey } from '../utils/pitchLabel';
import { registerTonicMidi } from '../utils/earTrainingSolfegeTokens';
import { formatKeyLabelShort } from '../utils/solfege';

/** Ear training: curated tonal clips, play, solfege puzzle, reveal. */
export class EarTrainingView {
  private player = new EarTrainingPlayer();
  private clip: EarTrainingClip | null = null;
  private revealed = false;
  private playing = false;
  private answerSyllables: string[] = [];
  private container: HTMLElement | null = null;
  private prefs: EarTrainingPreferences = loadEarTrainingPreferences();
  private settingsOutsideClick: ((e: MouseEvent) => void) | null = null;

  render(container: HTMLElement): void {
    this.container = container;
    this.clip = null;
    this.revealed = false;
    this.playing = false;
    this.answerSyllables = [];
    this.prefs = loadEarTrainingPreferences();

    const instrumentOptions = (
      Object.entries(INSTRUMENT_LABELS) as [EarTrainingInstrument, string][]
    )
      .map(
        ([id, label]) =>
          `<option value="${id}"${this.prefs.instrument === id ? ' selected' : ''}>${escapeHtml(label)}</option>`
      )
      .join('');

    const counts = difficultyCounts();
    const difficultyOptions = Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      const c = counts[n] ?? 0;
      const label = c === 0 ? `Level ${n} (none yet)` : c === 1 ? `Level ${n} (1 tune)` : `Level ${n} (${c} tunes)`;
      return `<option value="${n}"${n === this.prefs.maxDifficulty ? ' selected' : ''}>${escapeHtml(label)}</option>`;
    }).join('');

    const settingsHidden = this.prefs.settingsExpanded ? '' : ' hidden';

    container.innerHTML = `
      <div class="ear-training-page">
        <h2 class="file-title ear-training-title">Ear Training</h2>
        <div class="ear-training-controls-row">
          <div class="ear-training-toolbar" role="toolbar" aria-label="Melody controls">
            <button type="button" class="ear-training-tb-btn" id="ear-generate" title="New melody" aria-label="New melody">?</button>
            <button type="button" class="ear-training-tb-btn" id="ear-play" disabled title="Play melody" aria-label="Play melody">▶</button>
            <button type="button" class="ear-training-tb-btn" id="ear-reveal" disabled title="Reveal answer" aria-label="Reveal answer">✓</button>
          </div>
          <div class="ear-training-settings-anchor">
            <button
              type="button"
              class="ear-training-tb-btn ear-training-settings-toggle${this.prefs.settingsExpanded ? ' ear-training-settings-toggle--open' : ''}"
              id="ear-settings-toggle"
              title="Settings"
              aria-label="Settings"
              aria-expanded="${this.prefs.settingsExpanded ? 'true' : 'false'}"
              aria-controls="ear-settings-panel"
            >⚙</button>
            <div class="ear-training-settings-panel${settingsHidden}" id="ear-settings-panel" role="region" aria-label="Ear training settings">
              <p class="ear-training-settings-heading">Settings</p>
              <div class="ear-training-settings-body">
                <label class="ear-training-option">
                  <input type="checkbox" id="ear-show-key" ${this.prefs.showKeyLetter ? 'checked' : ''} />
                  Key
                </label>
                <label class="ear-training-option">
                  <input type="checkbox" id="ear-show-start" ${this.prefs.showStartingNote ? 'checked' : ''} />
                  Start note
                </label>
                <label class="ear-training-option ear-training-option-instrument">
                  Instrument
                  <select id="ear-instrument" class="ear-training-select">${instrumentOptions}</select>
                </label>
                <label class="ear-training-option ear-training-option-instrument">
                  Difficulty
                  <select id="ear-difficulty" class="ear-training-select" title="Picks a tune at this level (e.g. 10 = hardest in the bank)">${difficultyOptions}</select>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="ear-training-hints hidden" id="ear-hints" aria-live="polite"></div>
        <section class="ear-training-puzzle hidden" id="ear-puzzle" aria-label="Solfege puzzle">
          <div class="ear-training-palette" id="ear-palette" role="group" aria-label="Solfege syllables"></div>
          <div class="ear-training-answer-row">
            <div class="ear-training-answer-box">
              <div class="ear-training-answer" id="ear-answer" aria-label="Your solfege answer"></div>
              <p class="ear-training-answer-placeholder" id="ear-answer-placeholder">Tap syllables to answer</p>
            </div>
            <button
              type="button"
              class="ear-training-tb-btn ear-training-play-answer hidden"
              id="ear-play-answer"
              title="Play your answer"
              aria-label="Play your answer"
              disabled
            >▶</button>
          </div>
          <div class="ear-training-puzzle-actions">
            <button type="button" class="btn-primary ear-training-action-btn" id="ear-check" disabled>Check</button>
            <button type="button" class="btn-ghost-dark ear-training-action-btn" id="ear-clear" disabled>Clear</button>
          </div>
          <p class="ear-training-feedback hidden" id="ear-feedback" role="status" aria-live="polite"></p>
        </section>
        <div class="ear-training-reveal hidden" id="ear-reveal-panel" aria-hidden="true">
          <p class="ear-training-source" id="ear-source"></p>
          <p class="ear-training-key" id="ear-key"></p>
          <div class="ear-training-labels" id="ear-labels"></div>
          <div class="ear-training-staff-wrap" id="ear-staff"></div>
        </div>
      </div>`;

    const settingsToggle = container.querySelector<HTMLButtonElement>('#ear-settings-toggle');
    const settingsPanel = container.querySelector<HTMLElement>('#ear-settings-panel');
    const syncSettingsPanel = (open: boolean) => {
      this.prefs.settingsExpanded = open;
      settingsPanel?.classList.toggle('hidden', !open);
      settingsToggle?.classList.toggle('ear-training-settings-toggle--open', open);
      if (settingsToggle) settingsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.persistPrefs();
    };
    settingsToggle?.addEventListener('click', e => {
      e.stopPropagation();
      syncSettingsPanel(!this.prefs.settingsExpanded);
    });

    this.settingsOutsideClick = (e: MouseEvent) => {
      if (!this.prefs.settingsExpanded) return;
      const target = e.target as Node;
      if (settingsPanel?.contains(target) || settingsToggle?.contains(target)) return;
      syncSettingsPanel(false);
    };
    document.addEventListener('click', this.settingsOutsideClick);

    container.querySelector<HTMLInputElement>('#ear-show-key')?.addEventListener('change', e => {
      this.prefs.showKeyLetter = (e.target as HTMLInputElement).checked;
      this.persistPrefs();
      this.updateHints();
    });
    container.querySelector<HTMLInputElement>('#ear-show-start')?.addEventListener('change', e => {
      this.prefs.showStartingNote = (e.target as HTMLInputElement).checked;
      this.persistPrefs();
      this.updateHints();
    });
    container.querySelector<HTMLSelectElement>('#ear-instrument')?.addEventListener('change', e => {
      this.prefs.instrument = (e.target as HTMLSelectElement).value as EarTrainingInstrument;
      this.persistPrefs();
    });
    container.querySelector<HTMLSelectElement>('#ear-difficulty')?.addEventListener('change', e => {
      this.prefs.maxDifficulty = Number((e.target as HTMLSelectElement).value);
      this.persistPrefs();
    });

    container.querySelector('#ear-palette')?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-syllable]');
      if (!btn) return;
      const syllable = btn.dataset.syllable;
      if (syllable) this.addSyllable(syllable);
    });

    container.querySelector('#ear-answer')?.addEventListener('click', e => {
      const remove = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-remove-index]');
      if (!remove) return;
      const idx = Number(remove.dataset.removeIndex);
      if (!Number.isNaN(idx)) this.removeSyllableAt(idx);
    });

    container.querySelector<HTMLButtonElement>('#ear-generate')?.addEventListener('click', () => {
      this.generate();
    });
    container.querySelector<HTMLButtonElement>('#ear-play')?.addEventListener('click', () => {
      void this.play();
    });
    container.querySelector<HTMLButtonElement>('#ear-reveal')?.addEventListener('click', () => {
      this.reveal();
    });
    container.querySelector<HTMLButtonElement>('#ear-check')?.addEventListener('click', () => {
      this.checkAnswer();
    });
    container.querySelector<HTMLButtonElement>('#ear-clear')?.addEventListener('click', () => {
      this.clearAnswer();
    });
    container.querySelector<HTMLButtonElement>('#ear-play-answer')?.addEventListener('click', () => {
      void this.playUserAnswer();
    });

    container.querySelector('#ear-hints')?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-ear-midi]');
      if (!btn?.dataset.earMidi) return;
      const midi = Number(btn.dataset.earMidi);
      if (!Number.isFinite(midi)) return;
      this.playHintNote(midi);
    });
  }

  dispose(): void {
    if (this.settingsOutsideClick) {
      document.removeEventListener('click', this.settingsOutsideClick);
      this.settingsOutsideClick = null;
    }
    this.player.dispose();
    this.container = null;
  }

  private persistPrefs(): void {
    saveEarTrainingPreferences(this.prefs);
  }

  private generate(): void {
    this.player.stop();
    this.clip = pickRandomClip(this.prefs.maxDifficulty);
    this.revealed = false;
    this.playing = false;
    this.answerSyllables = [];
    this.updateRevealPanel();
    this.updateHints();
    this.updatePuzzle();
    this.renderPalette();
    this.hideFeedback();
    this.syncButtons();
    this.syncAnswerPlayButton();
    void this.play();
  }

  private renderPalette(): void {
    const el = this.container?.querySelector<HTMLElement>('#ear-palette');
    if (!el || !this.clip) {
      if (el) el.innerHTML = '';
      return;
    }
    const syllables = buildPaletteForClip(this.clip);
    el.innerHTML = syllables
      .map(s => {
        const extra = isObscureSyllable(s) ? ' ear-training-syllable-extra' : '';
        return `<button type="button" class="ear-training-syllable${extra}" data-syllable="${escapeHtml(s)}">${renderSolfegeTokenHtml(s)}</button>`;
      })
      .join('');
  }

  private addSyllable(syllable: string): void {
    if (!this.clip || this.playing) return;
    this.answerSyllables.push(syllable);
    this.hideFeedback();
    this.renderAnswerPills();
    this.syncButtons();
    this.syncAnswerPlayButton();
  }

  private removeSyllableAt(index: number): void {
    if (index < 0 || index >= this.answerSyllables.length) return;
    this.answerSyllables.splice(index, 1);
    this.hideFeedback();
    this.renderAnswerPills();
    this.syncButtons();
    this.syncAnswerPlayButton();
  }

  private clearAnswer(): void {
    this.answerSyllables = [];
    this.hideFeedback();
    this.renderAnswerPills();
    this.syncButtons();
    this.syncAnswerPlayButton();
  }

  private checkAnswer(): void {
    if (!this.clip) return;
    const expected = getClipSolfegeAnswer(this.clip);
    const result = checkSolfegeAnswer(this.answerSyllables, expected);
    const el = this.container?.querySelector<HTMLElement>('#ear-feedback');
    if (!el) return;
    el.classList.remove('hidden', 'ear-training-feedback-ok', 'ear-training-feedback-bad');
    el.classList.add(result.correct ? 'ear-training-feedback-ok' : 'ear-training-feedback-bad');
    el.textContent = result.message;
  }

  private showFeedback(message: string, correct: boolean): void {
    const el = this.container?.querySelector<HTMLElement>('#ear-feedback');
    if (!el) return;
    el.classList.remove('hidden', 'ear-training-feedback-ok', 'ear-training-feedback-bad');
    el.classList.add(correct ? 'ear-training-feedback-ok' : 'ear-training-feedback-bad');
    el.textContent = message;
  }

  private hideFeedback(): void {
    const el = this.container?.querySelector<HTMLElement>('#ear-feedback');
    if (!el) return;
    el.classList.add('hidden');
    el.textContent = '';
    el.classList.remove('ear-training-feedback-ok', 'ear-training-feedback-bad');
  }

  private renderAnswerPills(): void {
    const answer = this.container?.querySelector<HTMLElement>('#ear-answer');
    const placeholder = this.container?.querySelector<HTMLElement>('#ear-answer-placeholder');
    if (!answer) return;

    if (this.answerSyllables.length === 0) {
      answer.innerHTML = '';
      placeholder?.classList.remove('hidden');
      return;
    }

    placeholder?.classList.add('hidden');
    answer.innerHTML = this.answerSyllables
      .map((s, i) => {
        const remove = `<button type="button" class="ear-training-pill-remove" data-remove-index="${i}" aria-label="Remove ${escapeHtml(s)}">×</button>`;
        return `<span class="ear-training-pill">${renderSolfegeTokenHtml(s)}${remove}</span>`;
      })
      .join('');
    this.syncAnswerPlayButton();
  }

  private updatePuzzle(): void {
    const puzzle = this.container?.querySelector<HTMLElement>('#ear-puzzle');
    if (!puzzle) return;
    if (!this.clip) {
      puzzle.classList.add('hidden');
      return;
    }
    puzzle.classList.remove('hidden');
    this.renderPalette();
    this.renderAnswerPills();
  }

  private playHintNote(midi: number): void {
    this.player.stop();
    this.playing = false;
    this.syncButtons();
    void this.player.playNote(midi, this.prefs.instrument).catch(err => {
      this.showFeedback(`Play failed: ${(err as Error).message}`, false);
    });
  }

  private async play(): Promise<void> {
    if (!this.clip || this.playing) return;
    this.playing = true;
    this.syncButtons();
    try {
      await this.player.play(this.clip, this.prefs.instrument, () => {
        this.playing = false;
        this.syncButtons();
      });
    } catch (err) {
      this.playing = false;
      this.showFeedback(`Play failed: ${(err as Error).message}`, false);
      this.syncButtons();
    }
  }

  private reveal(): void {
    if (!this.clip) return;
    this.revealed = true;
    this.renderAnswerPills();
    this.updateRevealPanel();
    this.syncButtons();
    this.syncAnswerPlayButton();
  }

  private syncAnswerPlayButton(): void {
    const btn = this.container?.querySelector<HTMLButtonElement>('#ear-play-answer');
    if (!btn) return;
    const hasAnswer = this.answerSyllables.length > 0;
    btn.classList.toggle('hidden', !hasAnswer);
    btn.disabled = !hasAnswer || this.playing || !this.clip;
  }

  private async playUserAnswer(): Promise<void> {
    if (!this.clip || this.answerSyllables.length === 0 || this.playing) {
      return;
    }
    const notes = buildUserAnswerPlaybackNotes(this.clip, this.answerSyllables);
    if (!notes) {
      this.showFeedback('Could not play your answer — unknown syllable.', false);
      return;
    }
    this.playing = true;
    this.syncButtons();
    this.syncAnswerPlayButton();
    try {
      await this.player.play(
        { ...this.clip, notes, transposeSemitones: this.clip.transposeSemitones },
        this.prefs.instrument,
        () => {
          this.playing = false;
          this.syncButtons();
          this.syncAnswerPlayButton();
        }
      );
    } catch (err) {
      this.playing = false;
      this.showFeedback(`Play failed: ${(err as Error).message}`, false);
      this.syncButtons();
      this.syncAnswerPlayButton();
    }
  }

  private updateHints(): void {
    const el = this.container?.querySelector<HTMLElement>('#ear-hints');
    if (!el) return;
    if (!this.clip) {
      el.classList.add('hidden');
      el.innerHTML = '';
      return;
    }
    const firstMidi = this.clip.notes[0]!.midi + this.clip.transposeSemitones;
    const parts: string[] = [];
    if (this.prefs.showKeyLetter) {
      const keyLabel = formatKeyLabelShort(this.clip.tonic, this.clip.mode);
      const tonicMidi = registerTonicMidi(firstMidi, this.clip.tonic);
      parts.push(
        `<button type="button" class="ear-training-hint-btn" data-ear-midi="${tonicMidi}" title="Play key note" aria-label="Play key note, ${escapeHtml(keyLabel)}"><strong>Key</strong> ${escapeHtml(keyLabel)}</button>`
      );
    }
    if (this.prefs.showStartingNote) {
      const startLabel = midiToNoteNameInKey(firstMidi, this.clip.keySignatureFifths);
      parts.push(
        `<button type="button" class="ear-training-hint-btn" data-ear-midi="${firstMidi}" title="Play starting note" aria-label="Play starting note, ${escapeHtml(startLabel)}"><strong>Start</strong> ${escapeHtml(startLabel)}</button>`
      );
    }
    if (parts.length === 0) {
      el.classList.add('hidden');
      el.innerHTML = '';
      return;
    }
    el.classList.remove('hidden');
    el.innerHTML = parts.join('<span class="ear-training-hint-sep" aria-hidden="true">·</span>');
  }

  private updateRevealPanel(): void {
    const panel = this.container?.querySelector<HTMLElement>('#ear-reveal-panel');
    if (!panel) return;
    if (!this.clip || !this.revealed) {
      panel.classList.add('hidden');
      panel.setAttribute('aria-hidden', 'true');
      return;
    }
    panel.classList.remove('hidden');
    panel.setAttribute('aria-hidden', 'false');

    const transpose = this.clip.transposeSemitones;
    const letters: string[] = [];
    const fifths = this.clip.keySignatureFifths;
    for (const n of this.clip.notes) {
      const midi = n.midi + transpose;
      letters.push(midiToNoteNameInKey(midi, fifths));
    }
    const solfege = getClipSolfegeAnswer(this.clip);

    const sourceEl = panel.querySelector<HTMLElement>('#ear-source');
    const keyEl = panel.querySelector<HTMLElement>('#ear-key');
    const labelsEl = panel.querySelector<HTMLElement>('#ear-labels');
    const staffEl = panel.querySelector<HTMLElement>('#ear-staff');
    if (sourceEl) {
      sourceEl.textContent = this.clip.sourceTitle;
    }
    if (keyEl) {
      let label = formatKeyLabelShort(this.clip.tonic, this.clip.mode);
      if (transpose !== 0) {
        label += ` · ${transpose > 0 ? '+' : ''}${transpose}`;
      }
      keyEl.textContent = label;
    }
    if (labelsEl) {
      labelsEl.innerHTML = `
        <p class="ear-training-line ear-training-line-pitches" aria-label="Pitch names">
          ${escapeHtml(formatHyphenLine(letters))}
        </p>
        <p class="ear-training-line ear-training-line-solfege" aria-label="Solfege">
          ${formatSolfegeTokensLineHtml(solfege)}
        </p>`;
    }
    if (staffEl) {
      const clip = this.clip;
      requestAnimationFrame(() => {
        try {
          renderEarTrainingStaff(staffEl, clip);
        } catch (err) {
          staffEl.innerHTML = `<p class="ear-training-staff-error">Could not draw staff: ${escapeHtml((err as Error).message)}</p>`;
        }
      });
    }
  }

  private syncButtons(): void {
    const play = this.container?.querySelector<HTMLButtonElement>('#ear-play');
    const reveal = this.container?.querySelector<HTMLButtonElement>('#ear-reveal');
    const check = this.container?.querySelector<HTMLButtonElement>('#ear-check');
    const clear = this.container?.querySelector<HTMLButtonElement>('#ear-clear');
    const hasClip = !!this.clip;
    if (play) {
      play.disabled = !hasClip || this.playing;
    }
    if (reveal) {
      reveal.disabled = !hasClip || this.revealed;
    }
    if (check) {
      check.disabled = !hasClip;
    }
    if (clear) {
      clear.disabled = !hasClip || this.answerSyllables.length === 0;
    }
    this.syncAnswerPlayButton();
  }
}
